import { compareVersions } from '@standardnotes/common';
import { CreateDecryptedBackupFileContextPayload, CreateEncryptedBackupFileContextPayload, isDecryptedPayload, isEncryptedPayload, isEncryptedTransferPayload, } from '@standardnotes/models';
import { Result } from '@standardnotes/domain-core';
import { Strings } from './Strings';
export class ImportData {
    constructor(items, sync, protections, encryption, payloads, history, _decryptBackFile, _getFilePassword) {
        this.items = items;
        this.sync = sync;
        this.protections = protections;
        this.encryption = encryption;
        this.payloads = payloads;
        this.history = history;
        this._decryptBackFile = _decryptBackFile;
        this._getFilePassword = _getFilePassword;
    }
    async execute(data, awaitSync = false) {
        const versionValidation = this.validateFileVersion(data);
        if (versionValidation.isFailed()) {
            return Result.fail(versionValidation.getError());
        }
        const decryptedPayloadsOrError = await this.decryptData(data);
        if (decryptedPayloadsOrError.isFailed()) {
            return Result.fail(decryptedPayloadsOrError.getError());
        }
        const valid = this.getValidPayloadsToImportFromDecryptedResult(decryptedPayloadsOrError.getValue());
        if (!(await this.protections.authorizeFileImport())) {
            return Result.fail('Import aborted');
        }
        const affectedUuids = await this.payloads.importPayloads(valid, this.history.getHistoryMapCopy());
        const promise = this.sync.sync();
        if (awaitSync) {
            await promise;
        }
        const affectedItems = this.items.findItems(affectedUuids);
        return Result.ok({
            affectedItems: affectedItems,
            errorCount: decryptedPayloadsOrError.getValue().length - valid.length,
        });
    }
    validateFileVersion(data) {
        if (data.version) {
            const result = this.validateVersion(data.version);
            if (result.isFailed()) {
                return Result.fail(result.getError());
            }
        }
        return Result.ok();
    }
    async decryptData(data) {
        let password;
        if (data.auth_params || data.keyParams) {
            const passwordResult = await this._getFilePassword.execute();
            if (passwordResult.isFailed()) {
                return Result.fail(passwordResult.getError());
            }
            password = passwordResult.getValue();
        }
        this.cleanImportData(data);
        const decryptedPayloadsOrError = await this._decryptBackFile.execute(data, password);
        if (decryptedPayloadsOrError.isFailed()) {
            return Result.fail(decryptedPayloadsOrError.getError());
        }
        return Result.ok(decryptedPayloadsOrError.getValue());
    }
    getValidPayloadsToImportFromDecryptedResult(results) {
        const decrypted = results.filter(isDecryptedPayload);
        const encrypted = results.filter(isEncryptedPayload);
        const vaulted = encrypted.filter((payload) => {
            return payload.key_system_identifier !== undefined;
        });
        const valid = [...decrypted, ...vaulted];
        return valid;
    }
    cleanImportData(data) {
        data.items = data.items.map((item) => {
            if (isEncryptedTransferPayload(item)) {
                return CreateEncryptedBackupFileContextPayload(item);
            }
            else {
                return CreateDecryptedBackupFileContextPayload(item);
            }
        });
    }
    /**
     * Prior to 003 backup files did not have a version field so we cannot
     * stop importing if there is no backup file version, only if there is
     * an unsupported version.
     */
    validateVersion(version) {
        const supportedVersions = this.encryption.supportedVersions();
        if (!supportedVersions.includes(version)) {
            return Result.fail(Strings.UnsupportedBackupFileVersion);
        }
        const userVersion = this.encryption.getUserVersion();
        if (userVersion && compareVersions(version, userVersion) === 1) {
            /** File was made with a greater version than the user's account */
            return Result.fail(Strings.BackupFileMoreRecentThanAccount);
        }
        return Result.ok();
    }
}
